/**
 * Trial Period Banner Component
 * Exibe informações sobre o período de teste (uso gratuito) na barra superior
 * Mostra dias restantes e valor estimado após o teste
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
  Tooltip,
  Badge
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  Warning as WarningIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as AttachMoneyIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { apiService } from '@services/api';
import logger from '@utils/logger';
import { useAuth } from '@contexts/AuthContext';
import { UserRole } from '@/types';
import InfoBannerDrawer from './InfoBannerDrawer';

interface TrialPeriodInfo {
  tenantId: string;
  trialPeriodDays: number;
  startDate: string;
  endDate: string;
  daysRemaining: number;
  isActive: boolean;
  isExpired: boolean;
  estimatedMonthlyPrice: number;
  currency: string;
  subscriptionActive?: boolean;
}

interface TrialPeriodBannerProps {
  isMobile?: boolean;
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

const TrialPeriodBanner: React.FC<TrialPeriodBannerProps> = ({ 
  isMobile: isMobileProp,
  isOpen = false,
  onOpen,
  onClose
}) => {
  const [info, setInfo] = useState<TrialPeriodInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const anchorElRef = useRef<HTMLElement | null>(null);
  const theme = useTheme();
  const isMobileFromTheme = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = isMobileProp ?? isMobileFromTheme;
  const { user } = useAuth();
  
  // Verificar se o usuário é admin
  const isAdmin = user?.role === UserRole.ADMIN || user?.roleId?.isAdmin === true;
  
  const handleIconClick = (event: React.MouseEvent<HTMLElement>) => {
    anchorElRef.current = event.currentTarget;
    onOpen?.();
  };

  const handleClose = () => {
    onClose?.();
  };

  const loadTrialPeriodInfo = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getTrialPeriodInfo();
      if (response.data.success && response.data.data) {
        setInfo(response.data.data);
      } else {
        setInfo(null);
      }
    } catch (error: unknown) {
      logger.debug('Error loading trial period info', error);
      setInfo(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTrialPeriodInfo();
  }, [loadTrialPeriodInfo]);

  // Não mostrar se:
  // - Está carregando
  // - Não há informações
  // - Assinatura está ativa (não está mais em trial)
  // - Teste já expirou
  // - Teste não está ativo
  // - Usuário não é admin
  if (
    loading || 
    !info || 
    info.subscriptionActive === true || 
    info.isExpired || 
    !info.isActive || 
    !isAdmin
  ) {
    return null;
  }

  const formatCurrency = (value: number, currency: string = 'BRL'): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency || 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getDaysRemainingText = (days: number): string => {
    if (days === 0) {
      return 'Último dia';
    }
    if (days === 1) {
      return '1 dia restante';
    }
    return `${days} dias restantes`;
  };

  const _getProgressPercentage = (): number => {
    const totalDays = info.trialPeriodDays;
    const daysUsed = totalDays - info.daysRemaining;
    return Math.min(100, Math.max(0, (daysUsed / totalDays) * 100));
  };

  const isWarning = info.daysRemaining <= 7;
  const isCritical = info.daysRemaining <= 3;

  const headerBackground = 'linear-gradient(90deg, #f7f8fc 0%, #d1d8e4 100%)';

  const headerIcon = isCritical ? (
    <WarningIcon sx={{ fontSize: 24 }} />
  ) : (
    <AccessTimeIcon sx={{ fontSize: 24 }} />
  );

  const content = (
    <>
        {/* Período do Teste */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
            Período
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CalendarIcon sx={{ color: 'primary.main', fontSize: 20 }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Início: {formatDate(info.startDate)}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Término: {formatDate(info.endDate)}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Dias Restantes */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
            Dias Restantes
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon sx={{ color: 'primary.main', fontSize: 20 }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {info.daysRemaining === 0
                ? 'Último dia do teste'
                : info.daysRemaining === 1
                ? '1 dia restante'
                : `${info.daysRemaining} dias restantes`}
            </Typography>
          </Box>
        </Box>

        {info.estimatedMonthlyPrice > 0 && (
          <>
            <Divider sx={{ my: 2 }} />

            {/* Preço após Teste */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                Após o Teste
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachMoneyIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatCurrency(info.estimatedMonthlyPrice, info.currency)}/mês
                </Typography>
              </Box>
            </Box>
          </>
        )}
    </>
  );

  return (
    <>
      {/* Ícone no AppBar */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mr: { xs: 0.5, sm: 1 } }}>
        <Tooltip title="Período de Teste" arrow>
          <IconButton
            onClick={handleIconClick}
            color="inherit"
            size="small"
            sx={{
              bgcolor: isCritical
                ? 'error.light'
                : isWarning
                ? 'warning.light'
                : '#21316a',
              color: isCritical
                ? 'error.contrastText'
                : isWarning
                ? 'warning.contrastText'
                : 'white',
              '&:hover': {
                bgcolor: isCritical
                  ? 'error.main'
                  : isWarning
                  ? 'warning.main'
                  : 'info.main',
                opacity: 0.9
              }
            }}
          >
            <Badge
              variant="dot"
              color={isCritical ? 'error' : isWarning ? 'warning' : 'info'}
              overlap="circular"
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              {isCritical ? (
                <WarningIcon sx={{ fontSize: 18 }} />
              ) : (
                <AccessTimeIcon sx={{ fontSize: 18 }} />
              )}
            </Badge>
          </IconButton>
        </Tooltip>
        <Typography
          variant="caption"
          sx={{
            fontSize: { xs: '0.55rem', sm: '0.6rem' },
            color: '#21316a',
            mt: 0.25,
            whiteSpace: 'nowrap',
            lineHeight: 1
          }}
        >
          Teste
        </Typography>
      </Box>

      <InfoBannerDrawer
        isMobile={isMobile}
        anchorEl={isOpen ? anchorElRef.current : null}
        drawerOpen={isOpen && isMobile}
        onClose={handleClose}
        headerIcon={headerIcon}
        headerTitle="Período de Teste"
        headerSubtitle={getDaysRemainingText(info.daysRemaining)}
        headerBackground={headerBackground}
      >
        {content}
      </InfoBannerDrawer>
    </>
  );
};

export default TrialPeriodBanner;

